/**
 * Initialise Tako Alert
 */

/* -----------------------------------------------------------------------------
 * Tako Alert
 * -------------------------------------------------------------------------- */
import { InstanceLoggerArgs } from '../types/logger';
import { getLogger } from './utils/logger-context';
import { resolveComponentScope } from './utils/resolveComponentScope';

const COMPONENT_NAME = 'Alert';

export const init = (unresolvedScope: string | Element | unknown) => {
    const logger = getLogger();
    const scope = resolveComponentScope(unresolvedScope);
    const defaultLoggerArgs = {
        componentName: COMPONENT_NAME,
        scope,
    };
    logger.debug(`Initialising ${COMPONENT_NAME} component.`, { scope });

    // ToDo: Remove tako-alert--dismissible with the next breaking change
    const allDismissibleTakoAlerts = scope.querySelectorAll<HTMLElement>(
        '.tako-alert[data-dismissible], .tako-alert--dismissible',
    );

    /**
     * tako-alert--dismissible class is @deprecated
     */
    const dismissableAlertsWithLegacyClass =
        scope.querySelectorAll<HTMLElement>('.tako-alert--dismissible');

    if (dismissableAlertsWithLegacyClass.length) {
        logger.warn(
            'The `tako-alert--dismissible` class is deprecated. Please use the `data-dismissible` attribute instead.',
            defaultLoggerArgs,
        );
    }

    const allTakoAlertsWithIcon = scope.querySelectorAll<HTMLElement>(
        '.tako-alert:not(.tako-alert--no-icon)',
    );

    // Adds an icon unless `--no-icon` class is provided
    allTakoAlertsWithIcon.forEach((takoAlert) => {
        try {
            const instanceLoggerArgs: InstanceLoggerArgs = {
                ...defaultLoggerArgs,
                element: takoAlert,
            };
            const classList = takoAlert.classList;

            // ToDo: Remove this snippet with the next breaking change
            // Checks for and removes semantic icons manually added using the old syntax
            if (
                takoAlert.classList.contains('tako-alert--icon') &&
                takoAlert.querySelector<HTMLElement>('.tako-icon')
            ) {
                takoAlert.removeChild(
                    takoAlert.querySelector<HTMLElement>('.tako-icon')!,
                );
                logger.warn(
                    'Semantic icons are added by default to tako alerts. For the correct syntax please refer to storybook: https://tako-html-kraken-tech.vercel.app/?path=/docs/tako-html-alert--docs',
                    instanceLoggerArgs,
                );
            }

            // Wraps all children in a tako-alert__content div
            if (takoAlert.childNodes.length > 0) {
                addAlertContentWrapper(takoAlert);
            }

            let icon: string;
            switch (true) {
                case classList.contains('tako-alert--info'):
                    icon = 'fa-circle-info';
                    break;
                case classList.contains('tako-alert--success'):
                    icon = 'fa-circle-check';
                    break;
                case classList.contains('tako-alert--warning'):
                    icon = 'fa-triangle-exclamation';
                    break;
                case classList.contains('tako-alert--danger'):
                    icon = 'fa-circle-exclamation';
                    break;
                default:
                    icon = 'fa-circle-question';
                    break;
            }

            const iconContainer = document.createElement('span');
            iconContainer.classList.add(
                'tako-icon',
                'tako-icon--md',
                'tako-alert__icon',
            );
            iconContainer.innerHTML = `<i class='far ${icon}'></i>`;
            takoAlert.prepend(iconContainer);
        } catch (error) {
            logger.error(error as string, {
                ...defaultLoggerArgs,
                element: takoAlert,
            });
        }
    });

    allDismissibleTakoAlerts.forEach((takoDismissibleAlert) => {
        try {
            const alertContent =
                takoDismissibleAlert.querySelector<HTMLElement>(
                    '.tako-alert__content',
                );
            // Wraps all children in a tako-alert__content div
            if (!alertContent && takoDismissibleAlert.childNodes.length > 0) {
                addAlertContentWrapper(takoDismissibleAlert);
            }

            const dismissButton = document.createElement('button');
            dismissButton.classList.add(
                'tako-button',
                'tako-button--ghost',
                'tako-button--icon',
                'tako-button--inherit',
                'tako-button--no-padding',
            );
            dismissButton.setAttribute('data-alert-dismiss', 'true');
            dismissButton.setAttribute('aria-label', 'Close');
            dismissButton.innerHTML = `<span class="tako-icon tako-icon--md"><i class="far fa-xmark" aria-hidden="true"></i></span>`;

            takoDismissibleAlert.appendChild(dismissButton);

            const dismissAlert = () => {
                takoDismissibleAlert.style.display = 'none';
            };

            dismissButton.addEventListener('click', dismissAlert);
        } catch (error) {
            logger.error(error as string, {
                ...defaultLoggerArgs,
                element: takoDismissibleAlert,
            });
        }
    });
};

const addAlertContentWrapper = (takoAlert: HTMLElement): void => {
    const contentContainer = document.createElement('div');
    contentContainer.classList.add('tako-alert__content');
    contentContainer.append(...takoAlert.childNodes);
    takoAlert.appendChild(contentContainer);
};
